import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase, ProductFamily } from '@ibk/shared-types';
import { useCompanyAddress, useEmployeesData } from '../../../../../hooks';
import { HiringStep, SidebarStatus, SidebarStep } from '../../../../../types/hiring';
import { formatLocationAddress } from '../../../../../utils/addressFormatter';
export function useHiringFeedbackSidebar() {
    const { t } = useTranslation();
    const location = useLocation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isPatEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_ENABLE_PAT);
    const { currentLocation } = useCompanyAddress();
    const { employeesData } = useEmployeesData();
    const productFamiliesFromState = useMemo(() => {
        const state = location.state;
        return state?.productFamilies;
    }, [location.state]);
    const productFamiliesForSidebar = useMemo(() => {
        if (!productFamiliesFromState || productFamiliesFromState.length === 0) {
            return undefined;
        }
        return productFamiliesFromState.map(family => family.toLowerCase());
    }, [productFamiliesFromState]);
    const sortProductFamilies = useCallback((families) => {
        return [...families].sort((a, b) => {
            const order = [ProductFamily.AUXILIO, ProductFamily.PAT];
            return order.indexOf(a) - order.indexOf(b);
        });
    }, []);
    const getProductFamilyLabel = useCallback((family) => {
        return family === ProductFamily.AUXILIO
            ? t('hiring.product-family.short.auxilio')
            : t('hiring.product-family.short.pat');
    }, [t]);
    const getSidebarSteps = useMemo(() => {
        const steps = [];
        if (isPatEnabled) {
            steps.push({
                id: SidebarStep.PRODUCT_FAMILY,
                label: t('hiring.step.product-family'),
                status: SidebarStatus.COMPLETE,
            });
        }
        steps.push({ id: SidebarStep.ADDRESS, label: t('hiring.step.address'), status: SidebarStatus.COMPLETE });
        steps.push({ id: SidebarStep.EMPLOYEES, label: t('hiring.step.employees'), status: SidebarStatus.COMPLETE });
        if (isPatEnabled && productFamiliesFromState && productFamiliesFromState.length > 0) {
            const sortedFamilies = sortProductFamilies(productFamiliesFromState);
            sortedFamilies.forEach((family, index) => {
                const stepNumber = SidebarStep.EMPLOYEES + 1 + index;
                const familyLabel = getProductFamilyLabel(family);
                steps.push({
                    id: stepNumber,
                    label: `${t('hiring.step.contract')} - ${familyLabel}`,
                    status: SidebarStatus.COMPLETE,
                });
            });
        }
        else {
            steps.push({ id: SidebarStep.CONTRACT, label: t('hiring.step.contract'), status: SidebarStatus.COMPLETE });
        }
        const lastStepNumber = steps.length + 1;
        const lastStepLabel = isPatEnabled ? t('hiring.step.complete-request') : t('hiring.step.request');
        steps.push({ id: lastStepNumber, label: lastStepLabel, status: SidebarStatus.COMPLETE });
        return steps;
    }, [isPatEnabled, productFamiliesFromState, sortProductFamilies, getProductFamilyLabel, t]);
    const currentStep = {
        id: HiringStep.TERMS,
        title: isPatEnabled ? t('hiring.step.complete-request') : t('hiring.step.request'),
        number: getSidebarSteps.length,
        status: SidebarStatus.ACTIVE,
    };
    const breadcrumbs = [
        { label: t('hiring.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('hiring.breadcrumb.hiring'), isActive: true },
    ];
    const sidebarData = {
        steps: getSidebarSteps,
        address: currentLocation ? formatLocationAddress(currentLocation) : undefined,
        employeeCount: employeesData?.employeeCount,
        productFamilies: productFamiliesForSidebar,
    };
    return {
        sidebarData,
        currentStep,
        breadcrumbs,
    };
}
